import React, { useReducer, useContext } from "react";
import reducer from "./reducer";
import {
    DISPLAY_ALERT,
    CLEAR_ALERT,
    LOGOUT_USER_BEGIN,
    LOGOUT_USER_SUCCESS,
    CREATE_COMPANY_BEGIN,
    CREATE_COMPANY_SUCCESS,
    CREATE_COMPANY_ERROR,
} from "./actions";
import { useMutation } from "react-query";
import IdApi from "../service/IdApi";
import LoginApi from "../service/LoginApi";
import { usStates, countries } from "../utils/AddressHelper";
import TokenService from "../utils/TokenService";
import { Mixpanel } from "../utils/Mixpanel";
import ClientHubTokenService from "../utils/ClientHubTokenService";
import LocalStorageService from "../utils/LocalStorageService";

const userName = TokenService.getUserName();
export const initialState = {
    showAlert: false,
    alertSummary: "",
    alertDetail: "",
    alertType: "",
    isProcessing: false,
    userName: userName,
    companyId: null,
    loginEnabled: process.env.REACT_APP_LOGIN_ENABLED === "true",
    betaInterestEnabled: process.env.REACT_APP_BETA_INTEREST_ENABLED === "true",
    schedulerEnabled: process.env.REACT_APP_SCHEDULER_ENABLED === "true",
    dashboardEnabled: process.env.REACT_APP_DASHBOARD_ENABLED === "true",
    jobsEnabled: process.env.REACT_APP_JOBS_ENABLED === "true",
    addUsersEnabled: process.env.REACT_APP_ADD_USERS_ENABLED === "true",
    groupsEnabled: process.env.REACT_APP_GROUPS_ENABLED === "true",
    rolesEnabled: process.env.REACT_APP_ROLES_ENABLED === "true",
    paidPlanEnabled: process.env.REACT_APP_PAID_PLAN_ENABLED === "true",
    countries: countries,
    usStates: usStates,
};

const AppContext = React.createContext();

const AppProvider = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, initialState);

    const displayAlert = (severity, summary, detail) => {
        dispatch({ type: DISPLAY_ALERT, severity, summary, detail });
    };

    const clearAlert = () => {
        dispatch({
            type: CLEAR_ALERT,
        });
    };

    const logoutUser = async () => {
        if (TokenService.isAuthenticated()) {
            dispatch({ type: LOGOUT_USER_BEGIN });
            Mixpanel.reset();
            try {
                const response = await LoginApi.get("auth/logout", {
                    withCredentials: true,
                });
                // Always wait for provider logout before clearing tokens / state
                await LocalStorageService.logoutProvider();
                TokenService.logoutUser();
                dispatch({ type: LOGOUT_USER_SUCCESS });
                Mixpanel.track("UI Logout - successful");
                return response.data;
            } catch (error) {
                // Even if the API call fails, still clear local provider data and tokens
                await LocalStorageService.logoutProvider();
                TokenService.logoutUser();
                dispatch({ type: LOGOUT_USER_SUCCESS });
            }
        }

        if (ClientHubTokenService.isAuthenticated()) {
            ClientHubTokenService.logoutClientHub();
        }
    };

    const createCompanyApi = useMutation((values) => {
        try {
            const data = IdApi.post("auth/provider", values, {
                withCredentials: true,
            }).then((response) => response.data);
            return data;
        } catch (error) {
            throw error;
        }
    });

    const createCompany = (values) => {
        dispatch({ type: CREATE_COMPANY_BEGIN });
        const { name } = values;
        createCompanyApi.mutate(values, {
            onError: (error, variables, context) => {
                // TODO there might be limits and other types of errors. We should let the user know what the error is
                displayAlert("error", "Unexpected Error", "Please try again later");
                // Mixpanel.track("Create Provider - failed API error");
                dispatch({ type: CREATE_COMPANY_ERROR });
            },
            onSuccess: (data) => {
                TokenService.setAccessToken(data.userAccessToken);
                TokenService.setRefreshToken(data.userRefreshToken);
                displayAlert("success", "Success", "Company created successfully");
                // Mixpanel.track("Create Provider - successful");
                dispatch({ type: CREATE_COMPANY_SUCCESS, companyName: name });
                if (TokenService.getProviderSubdomain()) {
                    window.location.href = TokenService.getProviderAppUrl();
                }
            },
        });
    };

    const setUserName = (userName) => {
        state.userName = userName;
    }

    return (
        <AppContext.Provider
            value={{
                ...state,
                displayAlert,
                clearAlert,
                logoutUser,
                createCompany,
                setUserName,
            }}
        >
            {children}
        </AppContext.Provider>
    );
};
export const useAppContext = () => {
    return useContext(AppContext);
};

export { AppProvider };
